/**
 * Foundation/Core — Web Push client (factory parametrizzato)
 *
 * Client agnostico per Web Push API (RFC 8292 · VAPID). Nessun riferimento a
 * Product Pack: il chiamante fornisce namespace localStorage, path del Service
 * Worker, e — se necessario — categorie applicative come opaque payload.
 *
 * USO:
 *   const push = createWebPushClient({
 *     storageKey: "presenze_web_push_enabled", // deciso dal Product Pack
 *     swPath: "/service-worker.js",             // path SW registrato dal pack
 *   });
 *   await push.enable(categories);
 *   const status = await push.getStatus();
 *
 * ENDPOINT BACKEND FOUNDATION:
 *   /api/push/vapid-key  (GET)
 *   /api/push/subscribe   (POST)
 *   /api/push/unsubscribe (POST)
 *   /api/push/categories  (POST)
 * (Contratto server condiviso da tutti i Product Pack della Suite.)
 */
import { api } from "@/foundation/api/client";

const noop = () => {};

/** True se il browser supporta serviceWorker + PushManager + Notification. */
export function isWebPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Utility: base64 URL-safe → Uint8Array (formato richiesto da applicationServerKey). */
export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

/**
 * Crea un client Web Push parametrico. Ogni Product Pack ne istanzia uno con il
 * proprio `storageKey` (namespace localStorage) e `swPath` (Service Worker).
 */
export function createWebPushClient({ storageKey, swPath = "/service-worker.js" } = {}) {
  if (typeof storageKey !== "string" || !storageKey) {
    throw new Error("createWebPushClient: storageKey obbligatorio");
  }

  function setLocalEnabled(v) {
    try { localStorage.setItem(storageKey, v ? "1" : "0"); }
    catch (_e) { noop(); }
  }

  function getLocalEnabled() {
    try { return localStorage.getItem(storageKey) === "1"; }
    catch (_e) { return false; }
  }

  async function ensureServiceWorker() {
    if (!isWebPushSupported()) throw new Error("Web Push non supportato dal browser");
    const registration = await navigator.serviceWorker.register(swPath);
    await navigator.serviceWorker.ready;
    return registration;
  }

  async function getServerPublicKey() {
    const { data } = await api.get("/push/vapid-key");
    return data.public_key;
  }

  async function enable(categories) {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error(permission === "denied"
        ? "Il browser ha bloccato le notifiche. Autorizzale dalle impostazioni del sito."
        : "Permesso non concesso");
    }
    const registration = await ensureServiceWorker();
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const publicKey = await getServerPublicKey();
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const raw = subscription.toJSON();
    await api.post("/push/subscribe", {
      endpoint: raw.endpoint,
      keys: raw.keys,
      userAgent: navigator.userAgent,
      categories: categories || {},
    });
    setLocalEnabled(true);
    return { endpoint: raw.endpoint };
  }

  async function disable() {
    if (!isWebPushSupported()) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration(swPath);
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          try {
            await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
          } catch (_e) { /* prosegui: comunque disiscrivi lato browser */ }
          await subscription.unsubscribe();
        }
      }
    } finally { setLocalEnabled(false); }
  }

  async function getStatus() {
    if (!isWebPushSupported()) {
      return { supported: false, permission: "unsupported", subscribed: false };
    }
    const permission = Notification.permission;
    let subscribed = false;
    try {
      const registration = await navigator.serviceWorker.getRegistration(swPath);
      if (registration) {
        const s = await registration.pushManager.getSubscription();
        subscribed = !!s;
      }
    } catch (_e) { /* ignore */ }
    return { supported: true, permission, subscribed };
  }

  async function updateCategories(categories) {
    if (!isWebPushSupported()) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration(swPath);
      if (!registration) return;
      const s = await registration.pushManager.getSubscription();
      if (!s) return;
      await api.post("/push/categories", {
        endpoint: s.endpoint,
        categories: categories || {},
      });
    } catch (_e) { /* silenzioso */ }
  }

  return {
    isSupported: isWebPushSupported,
    getLocalEnabled,
    getServerPublicKey,
    enable,
    disable,
    getStatus,
    updateCategories,
  };
}
